import { PropsWithChildren } from 'react';

import { GetTokenSilentlyOptions, useAuth0 } from '@auth0/auth0-react';

import { isInvalidRefreshTokenError } from '~/lib/util/auth.util';

let getAccessTokenSilentlyFn: (
  options?: GetTokenSilentlyOptions | undefined,
) => Promise<string>;

let onTokenError: (() => void) | null = null;

export const security = {
  /**
   * Returns a wrapper around getAccessTokenSilently that automatically calls
   * the registered token error handler when Auth0's refresh token rotation
   * has invalidated the stored token (multi-tab race condition).
   */
  getAccessTokenSilently:
    () =>
    async (options?: GetTokenSilentlyOptions): Promise<string> => {
      try {
        return await getAccessTokenSilentlyFn(options);
      } catch (error) {
        if (isInvalidRefreshTokenError(error)) {
          onTokenError?.();
        }
        throw error;
      }
    },
  setAccessTokenSilently: (
    func: (options?: GetTokenSilentlyOptions | undefined) => Promise<string>,
  ) => (getAccessTokenSilentlyFn = func),
  /**
   * Register a handler called when a terminal token refresh error occurs.
   * Should redirect the user to the login page.
   */
  setTokenErrorHandler: (handler: () => void) => (onTokenError = handler),
};

export const ReduxSecurityWrapper = ({ children }: PropsWithChildren) => {
  const { getAccessTokenSilently, logout } = useAuth0();
  security.setAccessTokenSilently(getAccessTokenSilently);
  security.setTokenErrorHandler(() =>
    logout({ logoutParams: { returnTo: window.location.origin } }),
  );

  return <>{children}</>;
};
